import { isClient } from "@voxmedia/duet-utils/client";

declare global {
  interface Window {
    dataLayer?: Record<string, string | number>[];
  }
}

export default class AnalyticsClient {
  constructor(emitStart = true) {
    if (!isClient()) {
      return;
    }

    window.dataLayer = window.dataLayer || [];

    if (emitStart) {
      window.dataLayer.push({
        "gtm.start": new Date().getTime(),
        event: "gtm.js",
      });
    }
  }

  emit(event: Record<string, any>): void {
    window.dataLayer?.push(event);
  }

  /**
   * Methods for handling the viewing of products (scorecards/product cards)
   * The intersection observer in each component will add to this queue
   */
  VIEW_PUSH_DELAY = 30000;

  viewedProductQueue: Record<string, any>[] = [];
  viewedProductPushTimeout: ReturnType<typeof setTimeout> | null = null;
  viewedProductVisibilityChangeHandled = false;

  unpublishedViewedProducts = (): Record<string, any>[] => {
    return this.viewedProductQueue.filter((p) => !p.pushed);
  };

  pushViewedProducts = (): void => {
    const newProducts = this.unpublishedViewedProducts();

    if (!newProducts.length) {
      return;
    }

    this.emit({
      event: "view_item",
      ecommerce: {
        items: newProducts,
      },
    });

    this.viewedProductQueue = this.viewedProductQueue.map((p) => ({
      ...p,
      pushed: true,
    }));
    this.viewedProductPushTimeout = null;
  };

  productViewed(product: Record<string, any>): void {
    // Add to queue if not already in there
    if (
      this.viewedProductQueue.filter(
        (p) =>
          p.item_name === product.item_name &&
          p.item_category2 === product.item_category2,
      ).length > 0
    ) {
      return;
    }

    this.viewedProductQueue.push(product);

    // If we reach 15 product views, push them to prevent hitting any data limits
    if (this.unpublishedViewedProducts().length >= 15) {
      this.pushViewedProducts();
      return;
    }

    // Kick off a timeout if one hasn't been started
    if (!this.viewedProductPushTimeout) {
      this.viewedProductPushTimeout = setTimeout(
        this.pushViewedProducts,
        this.VIEW_PUSH_DELAY,
      );
    }

    // Handle visibility change event (triggers when navigating to new page/leaving tab)
    if (!this.viewedProductVisibilityChangeHandled) {
      this.viewedProductVisibilityChangeHandled = true;
      document.addEventListener("visibilitychange", this.pushViewedProducts);
    }
  }
}
